import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notificationsApi } from './api';
import type { NotificationQuery } from './types';

const KEY = 'notifications';

export function useNotifications(query: NotificationQuery) {
  return useQuery({
    queryKey: [KEY, 'list', query],
    queryFn: () => notificationsApi.list(query),
  });
}

export function useUnreadCount() {
  return useQuery({
    queryKey: [KEY, 'unread-count'],
    queryFn: () => notificationsApi.unreadCount(),
    // A modest poll as a fallback to the socket push (spec §25).
    refetchInterval: 60_000,
  });
}

/** Invalidate every notification query (list + unread count). */
export function useInvalidateNotifications() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: [KEY] });
}

export function useMarkRead() {
  const invalidate = useInvalidateNotifications();
  return useMutation({
    mutationFn: (id: string) => notificationsApi.markRead(id),
    onSuccess: () => invalidate(),
  });
}

export function useMarkAllRead() {
  const invalidate = useInvalidateNotifications();
  return useMutation({
    mutationFn: () => notificationsApi.markAllRead(),
    onSuccess: () => invalidate(),
  });
}

export function useRemoveNotification() {
  const invalidate = useInvalidateNotifications();
  return useMutation({
    mutationFn: (id: string) => notificationsApi.remove(id),
    onSuccess: () => invalidate(),
  });
}

export function usePreferences() {
  return useQuery({
    queryKey: [KEY, 'preferences'],
    queryFn: () => notificationsApi.getPreferences(),
  });
}

export function useSetPreference() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { type: string; inApp?: boolean; email?: boolean }) =>
      notificationsApi.setPreference(body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [KEY, 'preferences'] }),
  });
}
